import { scrubEventProperties } from "./pii.js";
import type { AnalyticsEvent, AnalyticsEventName, AnalyticsProperties, AnalyticsSink } from "./types.js";

export interface AnalyticsClientConfig {
  /** Sinks that always receive events regardless of analytics consent - reserved for destinations that never leave the browser/server the estimator itself runs on (e.g. the console sink in dev). Never put a third-party destination here. */
  alwaysOnSinks?: AnalyticsSink[];
  /** Sinks gated behind analytics consent - GA4 and any other third-party destination belongs here, never in `alwaysOnSinks`. */
  consentGatedSinks?: AnalyticsSink[];
  /** Returns whether analytics consent is currently granted. Read fresh on every `track()` call rather than captured once, so a consent change mid-session takes effect immediately without recreating the client. */
  hasConsent: () => boolean;
  now?: () => Date;
}

/**
 * The single entry point the UI uses to record an event. Every event, on
 * every call, is: (1) PII-scrubbed (see pii.ts) before it reaches any
 * sink, including always-on ones, and (2) fanned out to always-on sinks
 * unconditionally and to consent-gated sinks only when `hasConsent()`
 * currently returns true. `track` never throws - a sink implementation
 * failure is the sink's responsibility to swallow (see Ga4AnalyticsSink),
 * but this method doesn't propagate anything either way.
 */
export class AnalyticsClient {
  private readonly alwaysOnSinks: AnalyticsSink[];
  private readonly consentGatedSinks: AnalyticsSink[];
  private readonly hasConsent: () => boolean;
  private readonly now: () => Date;

  constructor(config: AnalyticsClientConfig) {
    this.alwaysOnSinks = config.alwaysOnSinks ?? [];
    this.consentGatedSinks = config.consentGatedSinks ?? [];
    this.hasConsent = config.hasConsent;
    this.now = config.now ?? (() => new Date());
  }

  track(name: AnalyticsEventName, properties?: AnalyticsProperties, requestId?: string): void {
    const event: AnalyticsEvent = {
      name,
      timestamp: this.now().toISOString(),
      requestId,
      properties: scrubEventProperties(properties)
    };

    for (const sink of this.alwaysOnSinks) {
      try {
        sink.track(event);
      } catch {
        // An always-on sink (e.g. console logging) must never break the app either.
      }
    }

    if (this.hasConsent()) {
      for (const sink of this.consentGatedSinks) {
        try {
          sink.track(event);
        } catch {
          // Same guarantee for third-party destinations.
        }
      }
    }
  }
}
